import { useEffect, useState } from 'react';

import { AgencyApi, CfsApi, KaseApi, UserApi } from '@/api';
import { KasePropertyApi } from '@/api/api.kase.property';
import { VendorApi } from '@/api/api.vendor';
import { SelectItem } from '@/components/FormElements/Select/Select';
import { useKaseContext } from '@/hooks';
import { INarrative, IOffense, IPenalCode, IProperty, IUser } from '@/models';
import { IVendorType } from '@/models/vendors';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';

const VENDOR_COURTS_DISABLED_MESSAGE =
  'Please enable vendors. This is required for both Return Court and Arraignment Court fields to display values.';

interface UseArrestEditDataProps {
  kaseId: string;
  currentArrestee?: any;
}

interface UseArrestEditDataReturn {
  users: IUser[];
  agencies: SelectItem[];
  courts: SelectItem[];
  closedCfsOptions: SelectItem[];
  penalCodes: IPenalCode[];
  offensesByPenalCode: Map<string, IOffense>;
  associatedPersons: any[];
  narratives: INarrative[];
  caseProperties: IProperty[];
  otherArrests: { arrestId: string; masterNameId: string }[];
  fetchNarratives: () => Promise<void>;
  refetchCourts: () => Promise<SelectItem[]>;
  isLoading: boolean;
}

export const useArrestEditData = ({
  kaseId,
  currentArrestee,
}: UseArrestEditDataProps): UseArrestEditDataReturn => {
  const { updateToast } = useToastStore();
  const { userAgencyId, account } = useUserStore();

  const { kase } = useKaseContext();

  const [users, setUsers] = useState<IUser[]>([]);
  const [agencies, setAgencies] = useState<SelectItem[]>([]);
  const [courts, setCourts] = useState<SelectItem[]>([]);
  const [closedCfsOptions, setClosedCfsOptions] = useState<SelectItem[]>([]);
  const [penalCodes, setPenalCodes] = useState<IPenalCode[]>([]);
  const [offensesByPenalCode, setOffensesByPenalCode] = useState<
    Map<string, IOffense>
  >(new Map());
  const [associatedPersons, setAssociatedPersons] = useState<any[]>([]);
  const [narratives, setNarratives] = useState<INarrative[]>([]);
  const [caseProperties, setCaseProperties] = useState<IProperty[]>([]);
  const [otherArrests, setOtherArrests] = useState<
    { arrestId: string; masterNameId: string }[]
  >([]);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isVendorsEnabled = account?.settings?.features?.isEnabledVendors;

  const ensureVendorsEnabledForCourts = (): boolean => {
    if (isVendorsEnabled) {
      return true;
    }

    updateToast({
      type: ConfirmTypes.WARNING,
      open: true,
      message: VENDOR_COURTS_DISABLED_MESSAGE,
    });
    return false;
  };

  const fetchUsers = async () => {
    try {
      const { data } = await UserApi.get({ limit: 1000 });
      setUsers(data.results);
    } catch (err: any) {
      updateToast({
        type: ConfirmTypes.ERROR,
        open: true,
        message: err?.message || 'Failed to fetch users',
      });
    }
  };

  const fetchAgenciesAndCourts = async () => {
    try {
      // Fetch agencies and filter out current user's agency
      const agenciesResponse = await AgencyApi.getAgencies();
      const agencyOptions = agenciesResponse.data.results
        .filter((agency: any) => {
          // Filter out current user's agency if userAgencyId is available
          if (!userAgencyId) {
            return true; // Include all if no user agency ID
          }
          return agency._id?.toString() !== userAgencyId;
        })
        .map((agency: any) => ({
          label: agency.ori ? `${agency.ori} - ${agency.name}` : agency.name,
          value: agency._id?.toString() || agency.ori,
        }));
      setAgencies(agencyOptions);

      if (!ensureVendorsEnabledForCourts()) {
        setCourts([]);
        return;
      }

      // Fetch vendor types first to get court type
      const vendorTypesRes = await VendorApi.getTypes();
      const courtTypes = vendorTypesRes.data.filter(
        (vendorType: IVendorType) => vendorType.name.toLowerCase() === 'court',
      );

      // Fetch courts from Vendors with TYPE=COURT using vendor type filter
      if (courtTypes.length > 0) {
        const filter = JSON.stringify({
          type: [...courtTypes],
          isActive: true,
        });
        const courtsResponse = await VendorApi.list({
          limit: 1000,
          filter,
        });
        const courtOptions = courtsResponse.data.results.map((vendor: any) => ({
          label: vendor.name,
          value: String(vendor._id),
        }));
        setCourts(courtOptions);
      }
    } catch (error: any) {
      updateToast({
        type: ConfirmTypes.ERROR,
        open: true,
        message: error?.message || 'Failed to fetch agencies or courts',
      });
      setAgencies([]);
      setCourts([]);
    }
  };

  const fetchPenalCodes = async () => {
    try {
      const { data } = await KaseApi.getOffenses(kaseId, {
        limit: 1000,
      });

      const offensesMap = new Map<string, IOffense>();
      const currPenalCodes = data.results.reduce<IPenalCode[]>(
        (acc, offense) => {
          if (offense?.penalCode) {
            acc.push(offense?.penalCode);
            // Store full offense by penal code ID for lookup
            offensesMap.set(offense?.penalCode?._id || '', offense);
          }
          return acc;
        },
        [],
      );
      setPenalCodes(currPenalCodes);
      setOffensesByPenalCode(offensesMap);
    } catch (err: any) {
      updateToast({
        type: ConfirmTypes.ERROR,
        open: true,
        message: err?.message || 'Failed to fetch penal codes',
      });
      setPenalCodes([]);
      setOffensesByPenalCode(new Map());
    }
  };

  const fetchAssociatedPersons = async () => {
    if (!kaseId) {
      return;
    }

    try {
      const { data } = await KaseApi.getLinkedMasterNames(kaseId, {});
      setAssociatedPersons(data);
    } catch (err: any) {
      updateToast({
        type: ConfirmTypes.ERROR,
        open: true,
        message: err?.message || 'Failed to fetch associated persons',
      });
      setAssociatedPersons([]);
    }
  };

  const fetchCaseProperties = async () => {
    if (!kaseId) {
      return;
    }

    try {
      const { data } = await KasePropertyApi.list(kaseId, { limit: 1000 });
      setCaseProperties(data);
    } catch (err: any) {
      updateToast({
        type: ConfirmTypes.ERROR,
        open: true,
        message: err?.message || 'Failed to fetch case properties',
      });
    }
  };

  const fetchNarratives = async () => {
    const arresteeId = currentArrestee?.masterName?._id?.toString();
    if (!kaseId || !arresteeId) return;

    try {
      const response = await KaseApi.getArresteeNarratives(kaseId, arresteeId);
      setNarratives(Array.isArray(response.data) ? response.data : []);
    } catch {
      setNarratives([]);
    }
  };

  const fetchOtherArrests = async () => {
    if (!kaseId) {
      return;
    }

    try {
      const { data } = await KaseApi.getArrestees(kaseId, { limit: 1000 });
      const arrests = data.docs.map((arrestee) => ({
        arrestId: arrestee.arrestId || arrestee.number,
        masterNameId:
          typeof arrestee.masterName === 'string'
            ? arrestee.masterName
            : arrestee.masterName?._id?.toString() || '',
      }));
      setOtherArrests(arrests);
    } catch (error) {
      // Silently handle error - other arrests are optional
      setOtherArrests([]);
    }
  };

  const fetchClosedCfs = async () => {
    try {
      const response = await CfsApi.list({
        limit: 1000,
        page: 1,
        sort: JSON.stringify({ createdAt: -1 }),
        filter: JSON.stringify({
          items: [
            {
              field: 'isClosed',
              operator: 'equals',
              value: true,
            },
          ],
          logicOperator: 'and',
        }),
      });

      const options = (response.data.docs || []).map((cfs) => ({
        value: cfs._id || '',
        label: cfs.number || 'No CFS Number',
      }));

      if (kase?.cfsParent) {
        const foundCfs = options.find((cfs) => cfs.value === kase.cfsParent);
        if (!foundCfs) {
          // if the cfsparent cfs is not in the options list,
          // fetch the cfs data and add it to the beginning of the list
          const { data: cfs } = await CfsApi.getOne(kase.cfsParent);
          if (cfs) {
            options.unshift({
              value: cfs?._id?.toString() || '',
              label: cfs?.number || 'No CFS Number',
            });
          }
        }
      }

      setClosedCfsOptions(options);
    } catch (error: any) {
      updateToast({
        type: ConfirmTypes.ERROR,
        open: true,
        message: error?.message || 'Failed to fetch CFS records',
      });
      setClosedCfsOptions([]);
    }
  };

  const refetchCourts = async (): Promise<SelectItem[]> => {
    if (!ensureVendorsEnabledForCourts()) {
      setCourts([]);
      return [];
    }

    try {
      const vendorTypesRes = await VendorApi.getTypes();
      const courtTypes = vendorTypesRes.data.filter(
        (vendorType: IVendorType) => vendorType.name.toLowerCase() === 'court',
      );

      if (courtTypes.length > 0) {
        const filter = JSON.stringify({
          type: [...courtTypes],
          isActive: true,
        });
        const courtsResponse = await VendorApi.list({
          limit: 1000,
          filter,
        });
        const courtOptions = courtsResponse.data.results.map((vendor: any) => ({
          label: vendor.name,
          value: String(vendor._id),
        }));
        setCourts(courtOptions);
        return courtOptions;
      }
      return [];
    } catch (error: any) {
      updateToast({
        type: ConfirmTypes.ERROR,
        open: true,
        message: error?.message || 'Failed to fetch courts',
      });
      return [];
    }
  };

  // Fetch all data on mount
  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      await Promise.all([
        fetchUsers(),
        fetchAgenciesAndCourts(),
        fetchPenalCodes(),
        fetchAssociatedPersons(),
        fetchCaseProperties(),
        fetchNarratives(),
        fetchOtherArrests(),
        fetchClosedCfs(),
      ]);
      setIsLoading(false);
    };

    if (kaseId) {
      fetchAllData();
    }
  }, [kaseId, userAgencyId]);

  // Re-check property/evidence when currentArrestee changes
  useEffect(() => {
    if (kaseId && currentArrestee) {
      fetchCaseProperties();
    }
  }, [currentArrestee?.masterName?._id]);

  return {
    users,
    agencies,
    courts,
    closedCfsOptions,
    penalCodes,
    offensesByPenalCode,
    associatedPersons,
    narratives,
    caseProperties,
    otherArrests,
    fetchNarratives,
    refetchCourts,
    isLoading,
  };
};
